import {
  Box,
  Container,
  Typography,
  Stack,
  Button,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMinus, faXmark } from "@fortawesome/free-solid-svg-icons";

import { hexToRgba } from "@aclymatepackages/converters";

import Link from "../../../atoms/mui/Link";

import SectionHeader from "./SectionHeader";

const Mark = ({ value, palette }) => {
  if (value === true || value === "yes") {
    return (
      <FontAwesomeIcon
        icon={faCheck}
        style={{ color: palette.primary.main, fontSize: 18 }}
      />
    );
  }
  if (value === false || value === "no") {
    return (
      <FontAwesomeIcon
        icon={faXmark}
        style={{ color: palette.text.secondary, fontSize: 18, opacity: 0.5 }}
      />
    );
  }
  if (value === "partial") {
    return (
      <FontAwesomeIcon
        icon={faMinus}
        style={{ color: palette.text.secondary, fontSize: 18, opacity: 0.7 }}
      />
    );
  }
  return (
    <Typography variant="body2" color="textSecondary" align="center">
      {value || "—"}
    </Typography>
  );
};

const resolveCell = (values, rowIdx, colKey) => {
  if (!values) {
    if (colKey === "aclymate") return true;
    if (colKey === "consultants") return "partial";
    return false;
  }
  const row = values[rowIdx];
  if (!row) return null;
  return row[colKey];
};

const ColumnHeader = ({ col, palette }) => (
  <Box
    sx={{
      p: 2.5,
      bgcolor: col.highlighted
        ? palette.primary.main
        : palette.background.paper,
      color: col.highlighted ? palette.background.paper : palette.text.primary,
      textAlign: "center",
      borderRight: `1px solid ${palette.backgroundGray.dark}`,
      "&:last-child": { borderRight: 0 }
    }}
  >
    <Box mb={0.5}>
      <Typography
        variant="subtitle2"
        color={col.highlighted ? "inherit" : "textPrimary"}
      >
        {col.label}
      </Typography>
    </Box>
    <Box
      sx={{
        opacity: col.highlighted ? 0.85 : 1
      }}
    >
      <Typography
        variant="caption"
        color={col.highlighted ? "inherit" : "textSecondary"}
      >
        {col.description}
      </Typography>
    </Box>
  </Box>
);

const WhyComparison = ({
  eyebrow,
  title,
  description,
  columns,
  rows,
  values,
  primaryCta,
  secondaryCta,
  visual
}) => {
  const { palette } = useTheme();

  return (
    <Box sx={{ py: { xs: 6, md: 10 }, bgcolor: palette.backgroundGray.light }}>
      <SectionHeader
        eyebrow={eyebrow}
        title={title}
        description={description}
      />
      {visual && (
        <Container maxWidth="lg" sx={{ mb: { xs: 3, md: 5 } }}>
          <Box sx={{ borderRadius: 2, overflow: "hidden" }}>{visual}</Box>
        </Container>
      )}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: `200px repeat(${columns.length}, 1fr)`
            },
            gap: 0,
            border: `1px solid ${palette.backgroundGray.dark}`,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: palette.background.paper
          }}
        >
          <Box sx={{ display: { xs: "none", md: "block" } }} />
          {columns.map((col) => (
            <ColumnHeader key={`col-${col.key}`} col={col} palette={palette} />
          ))}

          {rows.map((row, rowIdx) => (
            <Box key={`row-${rowIdx}`} sx={{ display: "contents" }}>
              <Box
                sx={{
                  p: 2,
                  borderTop: `1px solid ${palette.backgroundGray.dark}`,
                  display: "flex",
                  alignItems: "center",
                  bgcolor: palette.backgroundGray.light
                }}
              >
                <Typography variant="subtitle2" color="textPrimary">
                  {row}
                </Typography>
              </Box>
              {columns.map((col) => (
                <Box
                  key={`cell-${rowIdx}-${col.key}`}
                  sx={{
                    p: 2,
                    borderTop: `1px solid ${palette.backgroundGray.dark}`,
                    borderRight: `1px solid ${palette.backgroundGray.dark}`,
                    "&:last-child": { borderRight: 0 },
                    bgcolor: col.highlighted
                      ? hexToRgba(palette.primary.main, 0.06)
                      : palette.background.paper,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: 56
                  }}
                >
                  <Mark
                    value={resolveCell(values, rowIdx, col.key)}
                    palette={palette}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        {(primaryCta || secondaryCta) && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            justifyContent="center"
            sx={{ mt: { xs: 4, md: 6 } }}
          >
            {primaryCta && (
              <Link href={primaryCta.href}>
                <Button variant="contained" color="primary" size="large">
                  {primaryCta.label}
                </Button>
              </Link>
            )}
            {secondaryCta && (
              <Link href={secondaryCta.href}>
                <Button variant="outlined" color="primary" size="large">
                  {secondaryCta.label}
                </Button>
              </Link>
            )}
          </Stack>
        )}
      </Container>
    </Box>
  );
};
export default WhyComparison;
